'use client';
import { TimezoneSettingsDialog } from 'components/TimezoneSettingsDialog';
import { Avatar } from 'components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from 'components/ui/sidebar';
import { useCurrentUser } from 'hooks/use-current-user';
import { BookOpen, ChevronsUpDown, Clock, FileText, LogOut, Mail, Shield } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import userSvg from '../assets/svg/circle-user-round.svg';

interface NavUserProps {
  user: {
    name: string
    email: string
  }
  onLogout: () => void;
}

export function NavUser ({
  user,
  onLogout,
}: NavUserProps) {
  const { isMobile } = useSidebar();
  const [tzOpen, setTzOpen] = useState(false);
  const currentUser = useCurrentUser().data;
  const userTimezone = currentUser?.timezone;
  const canAccessDataApi = currentUser?.settings?.can_access_data_api ?? false;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="h-10 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-6 w-6 rounded-lg">
                <img src={userSvg} alt={user.name} className="h-6 w-6 rounded-lg" />
              </Avatar>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-xs font-semibold">{user.name}</span>
                <span className="truncate text-[10px] text-sidebar-foreground/70">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-3.5" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left">
                <Avatar className="h-6 w-6 rounded-lg">
                  <img src={userSvg} alt={user.name} className="h-6 w-6 rounded-lg" />
                </Avatar>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-xs font-semibold">{user.name}</span>
                  <span className="truncate text-[10px] text-muted-foreground">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem className="text-xs" onSelect={() => setTzOpen(true)}>
                <Clock className="mr-2 size-3.5" />
                Timezone{userTimezone ? ` (${userTimezone})` : ''}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs" asChild>
                <a href="https://www.oilytics.co/terms-of-use" target="_blank" rel="noopener noreferrer">
                  <FileText className="mr-2 size-3.5" />
                  Terms of Service
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs" asChild>
                <a href="https://www.oilytics.co/privacy-policy" target="_blank" rel="noopener noreferrer">
                  <Shield className="mr-2 size-3.5" />
                  Privacy Policy
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs" asChild>
                <a href="https://www.oilytics.co/contact" target="_blank" rel="noopener noreferrer">
                  <Mail className="mr-2 size-3.5" />
                  Contact Us
                </a>
              </DropdownMenuItem>
              {canAccessDataApi && (
                <DropdownMenuItem className="text-xs" asChild>
                  <Link to="/data-api-docs" target="_blank" rel="noopener noreferrer">
                    <BookOpen className="mr-2 size-3.5" />
                    API Documentation
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-xs" onClick={onLogout}>
              <LogOut className="mr-2 size-3.5" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <TimezoneSettingsDialog
        open={tzOpen}
        onOpenChange={setTzOpen}
        currentTimezone={userTimezone}
      />
    </SidebarMenu>
  );
}
